'use client';
import {
  AspectRatio,
  Box,
  Flex,
  Grid,
  Image,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import { CollectionBenefitModal } from '~/components/collection/CollectionBenefitModal';
import { CollectionDetail } from '~/components/collection/CollectionDetail';
import { CollectionPrizeModal } from '~/components/collection/CollectionPrizeModal';
import { UnknownErrorContext } from '~/components/common/UnknownErrorContent';
import { BackFloatButton } from '~/components/common/buttons/BackFloatButton';
import { NotFoundPage } from '~/components/errors/NotFoundPage';
import { OpenPickupQRCodeModalButton } from '~/components/pickup/OpenPickupQRCodeModalButton';
import { PrizeTicketContainer } from '~/components/prize_tickets/PrizeTicketContainer';
import { useGachaIdInfo, useGachaIdPrizes } from '~/hooks/resources';
import { useGachaPrizeCategories } from '~/hooks/resources/gacha_id_prize_categories';
import { useGrantedItem } from '~/hooks/resources/granted_item';
import { useRedeemTicket } from '~/hooks/resources/redeem_ticket';
import { useI18nLn } from '~/hooks/store';
import { LoggedInLayout } from '~/layouts/LoggedInLayout';
import { usePrizeTickets } from '~/src/services/arcApi/prize_tickets';
import type { BenefitPrize } from '~/types/resources/benefitPrize';
import type { OrderPrize } from '~/types/resources/orderPrize';
import { Skeleton, SkeletonText } from '../ui/skeleton';

export const CollectionContainer: React.FC = () => {
  const { t } = useI18nLn();
  const unique_name = useParams<{ unique_name: string }>()?.unique_name;
  const { gachaInfo, statusCode, loading, error } = useGachaIdInfo(
    unique_name ?? ''
  );
  const { orderPrizes } = useGachaIdPrizes(gachaInfo?.id ?? '');
  const [orderPrize, setOrderPrize] = useState<OrderPrize | null>(null);
  const [benefitPrize, setBenefitPrize] = useState<BenefitPrize | null>(null);
  const { grantedItem } = useGrantedItem(gachaInfo?.id ?? '');
  const { prizeCategories } = useGachaPrizeCategories(gachaInfo?.id ?? '');
  const { prizeTickets } = usePrizeTickets();
  const { redeemTicket } = useRedeemTicket();
  const prizeDisclosure = useDisclosure();
  const benefitDisclosure = useDisclosure();
  const seoKeywords = useMemo(() => {
    const keywords = ['コレクション', 'collection'];
    if (gachaInfo != undefined) {
      keywords.push(gachaInfo.name);
      gachaInfo.unique_name != null && keywords.push(gachaInfo.unique_name);
    }
    return keywords;
  }, [gachaInfo]);

  const hasRedeemTicketWithGachaID =
    redeemTicket?.some(
      (redeemTicket) => redeemTicket.gacha_id === gachaInfo?.id
    ) ?? false;

  let item = <></>;
  if (loading && gachaInfo == null) {
    item = <EventCollectionPageSkeleton />;
  } else if (statusCode == 404) {
    item = <NotFoundPage />;
  } else if (
    error != null ||
    gachaInfo == null ||
    gachaInfo.status == 'hidden'
  ) {
    item = <UnknownErrorContext title="イベント情報の取得に失敗しました" />;
  } else {
    item = (
      <VStack w="full" m="16px">
        <BackFloatButton href={`/e/${unique_name}`} />
        <VStack w="full" mb={2}>
          <AspectRatio ratio={16 / 9} w="full" h="auto">
            <Image
              src={gachaInfo?.thumbnail_image_url}
              alt={`${gachaInfo?.name}のサムネイル画像`}
            />
          </AspectRatio>
          <Text
            fontSize="22px"
            fontWeight="bold"
            lineHeight="36px"
            textAlign="center"
            w="full"
          >
            Collection
          </Text>
        </VStack>
        <TabsRoot
          w="full"
          colorPalette="primary"
          defaultValue="collection"
          variant="outline"
          fitted
        >
          <TabsList>
            <TabsTrigger value="collection">
              {t('pages.gacha.collection')}
            </TabsTrigger>
            <TabsTrigger value="prize_tickets">
              {t('pages.gacha.ticket/coupon')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="collection">
            <CollectionDetail
              gachaDetail={gachaInfo}
              orderPrizes={orderPrizes}
              grantedItem={grantedItem ?? null}
              prizeCategories={prizeCategories ?? []}
              prizeOnOpen={prizeDisclosure.onOpen}
              setOrderPrize={setOrderPrize}
              benefitOnOpen={benefitDisclosure.onOpen}
              setBenefitPrize={setBenefitPrize}
              prizeTickets={prizeTickets}
              redeemTicket={
                hasRedeemTicketWithGachaID ? redeemTicket : undefined
              }
            />
          </TabsContent>
          <TabsContent value="prize_tickets">
            <Flex flexDir="column" bgColor="blank" w="full" p="16px" mb="16px">
              <Flex
                w="full"
                py="8px"
                display={hasRedeemTicketWithGachaID ? 'flex' : 'none'}
              >
                <OpenPickupQRCodeModalButton
                  gachaID={gachaInfo?.id ?? ''}
                  redeemTickets={redeemTicket}
                />
              </Flex>
              <PrizeTicketContainer gachaID={gachaInfo?.id ?? ''} />
            </Flex>
          </TabsContent>
        </TabsRoot>
        <CollectionPrizeModal
          isOpen={prizeDisclosure.open}
          onClose={prizeDisclosure.onClose}
          orderPrize={orderPrize}
          grantedItem={grantedItem ?? null}
          gachaDownloadSetting={gachaInfo.content_download}
        />
        <CollectionBenefitModal
          isOpen={benefitDisclosure.open}
          onClose={benefitDisclosure.onClose}
          benefitPrize={benefitPrize}
          grantedItem={grantedItem ?? null}
          gachaDownloadSetting={gachaInfo.content_download}
        />
      </VStack>
    );
  }

  return (
    <LoggedInLayout
      title={gachaInfo?.name ? `${gachaInfo?.name} - コレクション` : undefined}
      description={
        gachaInfo?.name
          ? `${gachaInfo.name} を引こう！獲得したアイテム(コレクション)を確認することができます`
          : undefined
      }
      keywords={seoKeywords.join(',')}
      image={gachaInfo?.thumbnail_image_url}
      url={`/e/${unique_name}/collection`}
    >
      {item}
    </LoggedInLayout>
  );
};

const EventCollectionPageSkeleton: React.FC = () => {
  return (
    <Flex flexDir="column" w="full" bgColor="white">
      <AspectRatio ratio={16 / 9}>
        <Skeleton width="full" />
      </AspectRatio>
      <Box h="16px" />
      <Flex px="16px" w="full" justifyContent="center">
        <SkeletonText height="36px" lineClamp={1} w="240px" />
      </Flex>
      <Box h="16px" />
      <Flex flexDir="column" bgColor="blank" w="full" p="16px">
        <Grid templateColumns="repeat(3, 1fr)" gap="8px" mx="8px">
          {[...Array(20)].map((_, index) => {
            return (
              <AspectRatio ratio={1} key={index}>
                <Skeleton h="full" w="full" />
              </AspectRatio>
            );
          })}
        </Grid>
        <Box h="80px" />
      </Flex>
    </Flex>
  );
};
